/**
 * @description
 * Composant de consultation et gestion des contrats de location (doc_type = LOCATION).
 * Affiche la liste des contrats avec leurs lignes de location rattachées, gère le filtrage multi-statuts,
 * magasin et client, ainsi que la sélection multiple pour facturation groupée.
 *
 * Dépendances :
 * - Services : QuotationsService, CommonService, LoginService, AlertService
 * - Composants partagés : MenuCommonComponent, SearchPartnerComponent
 * - Modèles : Sales_order, Sales_order_l, Store, Station, Partner
 */

import { Component, OnInit, AfterViewInit, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, catchError, switchMap } from 'rxjs';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe, DecimalPipe, NgClass } from '@angular/common';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { SearchPartnerComponent } from '@app/shared/search-partner/search-partner.component';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { DossiersIncertainsComponent } from './dossiers-incertains/dossiers-incertains.component'; // DOSSIERS INCERTAINS DE LA MIGRATION, TEMPORAIRE
import { QuotationsService, QuotationsFilter, PrepareRentalInvoicingResponse } from '@app/sales/quotations/quotations.service';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { RentalFseService, FseOrdersMapping } from '@app/rental/rental-fse.service';
import { RentalInvoicingDialogComponent, RentalInvoicingDialogResult, invoicingOutcome } from '@app/rental/rental-invoicing-dialog/rental-invoicing-dialog.component';
import { Store, Station, Partner } from '@app/shared/_models/common';
import { Sales_order, Sales_order_l } from '@app/shared/_models/sales';
import { RegisterGateService } from '@app/sales/periods/register-gate.service';
import { RentalBillingService, serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import type { RentalLifecycleFull, RentalLineView } from '@app/sales/rental-billing/rental-billing.model';
import { INTERRUPTED_BADGE, showInterruptedBadge, statusClass, statusLabel } from '@app/sales/rental-billing/rental-billing.labels';
import { partnerName } from '@app/sales/rental-contract/rental-patient';

import { SalesOrderState, OrderLineType } from '@app/shared/constants-server';
import { salesOrderLabels, salesOrderColors, getOrderLabel, getOrderColor, getStateKey, DocType } from '@app/shared/constants';

// Dossier de GET /sales/rental_orders : la pièce, plus la fiche de data.recipient quand il y en a une.
type RentalListOrder = Sales_order & { lnk_beneficiary?: { id: number; name?: string | null; firstname?: string | null } | null };

export interface StatusOption {
  value: SalesOrderState;
  label: string;
}

@Component({
  selector: 'app-rentals',
  standalone: true,
  templateUrl: './rentals.html',
  styleUrls: ['./rentals.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MenuCommonComponent,
    DossiersIncertainsComponent, // DOSSIERS INCERTAINS DE LA MIGRATION, TEMPORAIRE
    SearchPartnerComponent,
    FormsModule,
    RouterLink,
    NgClass,
    DatePipe,
    DecimalPipe,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDialogModule,
  ]
})
export class Rentals implements OnInit, AfterViewInit {
  public orders: Sales_order[] = [];
  public displayedOrders: Sales_order[] = [];

  /** Ensemble des IDs de locations actuellement sélectionnées pour facturation */
  public selectedOrderIds: Set<number> = new Set();

  // Dossiers repliés (seule la rangée du dossier reste) ; gardé d'une recherche à l'autre, les identifiants sont stables.
  public collapsedOrderIds: Set<number> = new Set();

  public displayedColumnsInfo: [string, string, string, boolean?][] = [
    ['id', 'Id', 'size1'],
    ['datecre', 'Dat. Cré', 'size2'],
    ['store', 'E', 'size1'],
    ['status', 'Statut', 'size1'],
    ['beneficiary', 'Bénéficiaire · livré', 'size2'], // toujours les deux, le bénéficiaire à gauche (JP 29/10)
    ['customer', 'Payeur · facturé', 'size2'],
    ['amount_taxi', 'Tot.', 'size1'],
    ['amount_remainer', 'Dû', 'size1'],
  ];

  public SalesOrderState = SalesOrderState;
  public salesOrderColors = salesOrderColors;
  public salesOrderLabels = salesOrderLabels;
  public getLabel = getOrderLabel;
  public getColor = getOrderColor;

  public stores: Store[] = [{ id: -1, name: 'Tous' }];
  public storeLegend = '';
  public stations: Station[] = [{ id: -1, name: 'Tous' }];

  // Plafond du serveur (GET /sales/rental_orders, model.rs : min(10_000)) : affiché tel qu'il est, jamais « Illimité » (audit 31/10 #57).
  public readonly SERVER_MAX = '10000';
  public limitsValue = ['50', '200', '1000', '5000', this.SERVER_MAX];

  public readonly availableStatuses: StatusOption[] = Object.values(SalesOrderState).map(s => ({
    value: s,
    label: getOrderLabel(s),
  }));

  public selectedStatuses: SalesOrderState[] = [];
  public hideReturned = true; // matériels repris (BL de reprise signé) non lus : un établissement sur 10 ans en a des milliers

  public Originalfilter: QuotationsFilter = {
    limit: this.limitsValue[0],
    doc_type: DocType.LOCATION,
    store: '-1',
  };
  public filter: QuotationsFilter = { ...this.Originalfilter };

  public customer: Partner | null = null;
  public is_loading = false;

  public commonService = inject(CommonService);
  private quotationsService = inject(QuotationsService);
  private loginService = inject(LoginService);
  private alertService = inject(AlertService);
  private spin = inject(SpinnerService);
  private rentalFseService = inject(RentalFseService);
  private dialog = inject(MatDialog);
  private registerGate = inject(RegisterGateService);
  private rentalBilling = inject(RentalBillingService);
  private destroyRef = inject(DestroyRef);
  // Une recherche relancée annule la précédente (#57) : jamais une réponse ancienne affichée après la dernière.
  private searches = new Subject<{ store?: number; customer?: number; states?: string; limit?: number; returned?: boolean }>();
  public readonly INTERRUPTED_BADGE = INTERRUPTED_BADGE;

  ngOnInit(): void {
    this.alertService.clear();
    // Erreur rattrapée dans le switchMap : le flux des recherches survit à une réponse en échec.
    this.searches.pipe(
      switchMap((q) => this.quotationsService.getRentalOrders(q).pipe(catchError((e) => {
        console.error('Erreur Serveur locations: ', e);
        this.is_loading = false;
        this.alertService.error('ERREUR Serveur : ' + (e?.error?.message || e?.error || e?.message || e));
        return EMPTY;
      }))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((items: Sales_order[]) => {
      this.orders = items ?? [];
      this.applyRentalStatusFilter();
      this.is_loading = false;
      this.refreshLifecycles();
    });

    const defaultStore = this.loginService.currentstore ?? this.loginService.loggeduser?.store;
    if (defaultStore !== undefined && defaultStore !== null) {
      const storeStr = defaultStore.toString();
      this.filter.store = storeStr;
      this.Originalfilter.store = storeStr;
    }

    this.commonService.getStores().subscribe({
      next: (stores: Store[]) => {
        this.stores = [...this.stores, ...stores];
        this.storeLegend = this.buildStoreLegend();
      }
    });

    this.commonService.getStations().subscribe({
      next: (stations: Station[]) => {
        this.stations = [...this.stations, ...stations];
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.onSearch());
  }

  onSearch(): void {
    this.is_loading = true;
    this.orders = [];
    this.displayedOrders = [];
    this.selectedOrderIds.clear();

    const store = this.filter.store ? parseInt(this.filter.store, 10) : undefined;
    const customer = this.filter.customer ? parseInt(this.filter.customer, 10) : undefined;
    // selectedStatuses vides = tous les états (pas de filtre d'état côté serveur)
    const states = this.selectedStatuses.length > 0 ? this.selectedStatuses.join(',') : undefined;
    const limit = this.filter.limit ? parseInt(this.filter.limit, 10) : undefined;

    this.searches.next({ store, customer, states, limit, returned: this.hideReturned ? false : undefined });
  }

  onRentalStatusChange(): void {
    this.applyRentalStatusFilter();
  }

  private applyRentalStatusFilter(): void {
    if (this.selectedStatuses && this.selectedStatuses.length > 0) {
      const allowed = new Set(this.selectedStatuses);
      this.displayedOrders = this.orders.filter(o => allowed.has(o.state));
    } else {
      this.displayedOrders = [...this.orders];
    }
  }

  customerChange(customer: Partner | null | undefined): void {
    if (customer && customer.id && customer.name) {
      this.filter.customer = customer.id.toString();
      this.customer = customer;
    } else {
      this.customer = null;
      delete this.filter.customer;
    }
    this.onSearch();
  }

  onCancel(): void {
    this.filter = { ...this.Originalfilter };
    this.customer = null;
    this.selectedStatuses = [];
    this.selectedOrderIds.clear();
    this.orders = [];
    this.displayedOrders = [];
    this.onSearch();
  }

  /**
   * Vérifie si un contrat de location est sélectionné.
   */
  public isOrderSelected(orderId: number): boolean {
    return this.selectedOrderIds.has(orderId);
  }

  /**
   * Alterne la sélection d'un contrat de location (les contrats annulés ne sont pas sélectionnables).
   */
  public toggleOrderSelection(orderId: number): void {
    const order = this.orders.find(o => o.id === orderId);
    if (order?.state === SalesOrderState.CANCELED) return;

    if (this.selectedOrderIds.has(orderId)) {
      this.selectedOrderIds.delete(orderId);
    } else {
      this.selectedOrderIds.add(orderId);
    }
  }

  /**
   * Sélectionne l'ensemble des locations actives actuellement affichées.
   */
  public selectAllOrders(): void {
    for (const o of this.displayedOrders) {
      if (o && o.id && o.state !== SalesOrderState.CANCELED) {
        this.selectedOrderIds.add(o.id);
      }
    }
  }

  /**
   * Désélectionne l'ensemble des locations.
   */
  public deselectAllOrders(): void {
    this.selectedOrderIds.clear();
  }

  /**
   * Indique si toutes les locations actives affichées sont sélectionnées.
   */
  public isAllSelected(): boolean {
    const selectable = this.displayedOrders.filter(o => o.state !== SalesOrderState.CANCELED);
    if (selectable.length === 0) return false;
    return selectable.every(o => this.selectedOrderIds.has(o.id));
  }

  /**
   * Indique si une partie seulement des locations actives affichées est sélectionnée (état indéterminé).
   */
  public isPartiallySelected(): boolean {
    const selectable = this.displayedOrders.filter(o => o.state !== SalesOrderState.CANCELED);
    if (selectable.length === 0) return false;
    const count = selectable.filter(o => this.selectedOrderIds.has(o.id)).length;
    return count > 0 && count < selectable.length;
  }

  /**
   * Alterne entre tout sélectionner et tout désélectionner.
   */
  public toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.deselectAllOrders();
    } else {
      this.selectAllOrders();
    }
  }

  public hasLines(order: Sales_order): boolean {
    return (order?.lines ?? []).some(l => l.typ !== OrderLineType.RENTAL_RETURN && (l.typ as any) !== 'RENTAL_RETURN');
  }

  public isCollapsed(orderId: number): boolean {
    return this.collapsedOrderIds.has(orderId);
  }

  // La flèche est dans la rangée-lien du dossier : sans preventDefault le clic ouvrirait aussi la page du dossier.
  public toggleCollapse(orderId: number, ev?: Event): void {
    ev?.stopPropagation();
    ev?.preventDefault();
    if (!this.collapsedOrderIds.delete(orderId)) this.collapsedOrderIds.add(orderId);
  }

  // Tout replié = aucun dossier affiché n'a de lignes visibles ; le bouton global propose alors de tout déplier.
  public allCollapsed(): boolean {
    const foldable = this.displayedOrders.filter(o => this.hasLines(o));
    return foldable.length > 0 && foldable.every(o => this.collapsedOrderIds.has(o.id));
  }

  public toggleCollapseAll(): void {
    if (this.allCollapsed()) {
      for (const o of this.displayedOrders) this.collapsedOrderIds.delete(o.id);
    } else {
      for (const o of this.displayedOrders) if (this.hasLines(o)) this.collapsedOrderIds.add(o.id);
    }
  }

  /**
   * Calcule le montant total TTC des locations sélectionnées.
   */
  public getSelectedTotal(): number {
    let total = 0;
    for (const o of this.orders) {
      if (o && o.id && this.selectedOrderIds.has(o.id)) {
        total += Number(o.amount_taxi || 0);
      }
    }
    return total;
  }

  /**
   * Action de facturation des locations sélectionnées.
   *
   * 1. Appel d'éligibilité et de préparation côté backend (prepareRentalInvoicing)
   * 2. Ouverture de la modale de progression et de sécurisation Area
   * 3. À la fermeture : notification du bilan et rafraîchissement de la liste
   */
  public onInvoice(): void {
    if (this.selectedOrderIds.size === 0) return;

    this.is_loading = true;

    this.registerGate.check$().subscribe(gate => {
      if (!gate.open) {
        this.is_loading = false;
        this.registerGate.redirectToPeriods(gate);
        return;
      }

      const orderIds = Array.from(this.selectedOrderIds);
      this.quotationsService
        .prepareRentalInvoicing(orderIds)
        .pipe(this.spin.block('Préparation de la facturation des locations…'))
        .subscribe({
        next: (resp: PrepareRentalInvoicingResponse) => {
          this.is_loading = false;

          const dialogRef = this.dialog.open(RentalInvoicingDialogComponent, {
            width: '90%',
            height: '85%',
            maxWidth: '95vw',
            disableClose: true,
            autoFocus: false,
            data: {
              eligibleInvoices: resp.eligible_invoices,
              unbillableOrders: resp.unbillable_orders,
            },
          });

          dialogRef.afterClosed().subscribe((result?: RentalInvoicingDialogResult) => {
            const out = invoicingOutcome(result);   // « réussi » = une pièce créée ; un dossier déjà à jour est dit tel quel (#33)
            if (!out) return;
            this.alertService[out.kind](out.text);
            if (result!.invoicedCount > 0) this.deselectAllOrders();
            this.onSearch();
          });
        },
        error: (err: any) => {
          this.is_loading = false;
          console.error('Erreur lors du contrôle d\'éligibilité:', err);
          this.alertService.error(
            'Erreur lors du contrôle d\'éligibilité : ' + (err?.error?.message || err?.message || err)
          );
        },
      });
    });
  }

  sortOrders(order: boolean, name: string): void {
    this.displayedOrders = this.displayedOrders.sort((a: Sales_order, b: Sales_order) => {
      let valA = this.getSortValue(a, name);
      let valB = this.getSortValue(b, name);
      const isNumeric = (v: any) => !isNaN(parseFloat(v)) && isFinite(v);
      if (isNumeric(valA) && isNumeric(valB)) {
        return order ? parseFloat(valA) - parseFloat(valB) : parseFloat(valB) - parseFloat(valA);
      } else {
        return order ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
      }
    });
    this.displayedColumnsInfo = this.displayedColumnsInfo.map(col =>
      col[0] === name ? [col[0], col[1], col[2], order] : [col[0], col[1], col[2]]
    );
  }

  private readonly STATE_RANK: string[] = Object.values(SalesOrderState);

  private rankOf(order: string[], value: any): number {
    const i = order.indexOf(String(value ?? '').toUpperCase());
    return i === -1 ? order.length : i;
  }

  public readonly statusSortHint: string =
    'Tri par cycle de vie du document (ni alphabétique, ni par date) :\n' +
    this.STATE_RANK.map((s, i) => `${i + 1}. ${getOrderLabel(s)}`).join('\n');

  colSortHint(colKey: string): string {
    if (colKey === 'status') return this.statusSortHint;
    if (colKey === 'store') return this.storeLegend;
    return '';
  }

  private buildStoreLegend(): string {
    const lignes = this.stores
      .filter(store => (store.id ?? -1) > 0)
      .map(store => `${store.id} — ${store.name ?? ''}`.trim());
    return lignes.length ? 'Établissements :\n' + lignes.join('\n') : '';
  }

  getSortValue(p: Sales_order, name: string): any {
    switch (name) {
      case 'beneficiary': return this.beneficiaryName(p);
      case 'customer': return this.payerName(p);
      case 'status': return this.rankOf(this.STATE_RANK, p.state);
      case 'amount_taxi': return Number(p.amount_taxi);
      case 'datecre': return p.dates?.created ? new Date(p.dates.created).getTime() : 0;
      case 'dateval': { const t = this.getLastDateTime(p); return isNaN(t) ? 0 : t; }
      case 'store': return p.lnk_station?.store ?? '';
      case 'amount_remainer': return this.getRemainer(p);
      default: return (p as any)[name];
    }
  }

  // Sans data.recipient, le client est à la fois livré et facturé ; un recipient sans fiche reste visible tel quel.
  beneficiaryName(o: Sales_order): string {
    const r = o.data?.recipient;
    if (r === undefined || r === null) return this.payerName(o);
    const b = (o as RentalListOrder).lnk_beneficiary;
    return b ? partnerName(b) : `Bénéficiaire introuvable (${r})`;
  }

  payerName(o: Sales_order): string {
    return partnerName(o.lnk_customer);
  }

  // Même fiche des deux côtés (pas de data.recipient, ou le client lui-même) : le payeur n'est pas recopié (JP 30/10).
  payerIsBeneficiary(o: Sales_order): boolean {
    const r = o.data?.recipient;
    return r === undefined || r === null || Number(r) === o.customer;
  }

  getRemainer(p: Sales_order): number {
    if (p.state === this.SalesOrderState.PAID) return 0;
    if (p && p.lnk_receivables && p.lnk_receivables.length > 0) {
      let expected = 0;
      let paid = 0;
      for (let i = 0; i < p.lnk_receivables.length; i++) {
        expected += +p.lnk_receivables[i].amount_expected;
        paid += +p.lnk_receivables[i].amount_paid;
      }
      let return_val = (Math.round((expected - paid) * 100) / 100);
      return return_val <= 0 ? 0 : return_val;
    }
    return p.amount_taxi;
  }

  getContractName(line: Sales_order_l): string {
    return line?.rental_terms_snapshot?.name ?? (line as any)?.rental_terms?.lnk_rental_terms?.name ?? '';
  }

  /**
   * Retourne la durée de location prévue par la prescription (en unités de période).
   * Lit `RentalLifecycle.qty` avec repli sur `rental_terms.qty`.
   */
  getRentalDuration(line: Sales_order_l): number | null {
    return line?.rental_lifecycle?.qty ?? (line as any)?.rental_terms?.qty ?? null;
  }

  // Durée absente ou ≤ 0 = illimitée, comme la F3 ; une ligne vendue n'a pas de Durée.
  durationLabel(line: Sales_order_l): string {
    if (!this.isRentalLine(line)) return '—';
    const d = Number(this.getRentalDuration(line) ?? 0);
    return d > 0 ? String(d) : '∞';
  }

  // Page B2B du dossier (Tableau, Planning, décisions), à comparer avec la F3.
  dossierLink(order: Sales_order): (string | number)[] {
    return ['/rental', order.id];
  }

  /**
   * Retourne l'ensemble des lignes du contrat parent (location et articles annexes/consommables).
   */
  getRentalLines(order: Sales_order): Sales_order_l[] {
    return (order?.lines ?? []).filter(l => l.typ !== OrderLineType.RENTAL_RETURN && (l.typ as any) !== 'RENTAL_RETURN');
  }

  /**
   * Indique si une ligne correspond à une prestation de location récurrente.
   */
  isRentalLine(line: Sales_order_l): boolean {
    return line?.typ === OrderLineType.RENTAL || (line?.typ as any) === 6 || line?.rental_terms_snapshot != null || (line as any)?.rental_terms != null;
  }

  // Le registre n'est plus dans le cycle de la ligne : l'avancement vient de la route du cycle de vie, chargée à la demande par dossier.
  public lifecycles = new Map<number, { loading: boolean; view?: RentalLifecycleFull; error?: string }>();

  loadLifecycle(order: Sales_order, ev?: Event): void {
    ev?.stopPropagation();
    ev?.preventDefault();
    this.fetchLifecycle(order.id);
  }

  // La vue précédente reste affichée pendant la relecture et après un échec ; seule la réponse suivante la remplace.
  private fetchLifecycle(id: number): void {
    const prev = this.lifecycles.get(id);
    if (prev?.loading) return;
    this.lifecycles.set(id, { ...prev, loading: true });
    this.rentalBilling.getLifecycle(id).subscribe({
      next: view => this.lifecycles.set(id, { loading: false, view }),
      error: e => this.lifecycles.set(id, { ...this.lifecycles.get(id), loading: false, error: serverMessage(e) }),
    });
  }

  // Après une recherche ou un passage : l'avancement déjà calculé des dossiers encore listés est relu, celui des autres oublié.
  private refreshLifecycles(): void {
    const listed = new Set(this.orders.map(o => o.id));
    for (const id of [...this.lifecycles.keys()]) {
      if (listed.has(id)) this.fetchLifecycle(id);
      else this.lifecycles.delete(id);
    }
  }

  lineLifecycle(order: Sales_order, line: Sales_order_l): RentalLineView | undefined {
    return this.lifecycles.get(order.id)?.view?.lines.find(l => l.id === line.id);
  }

  // Pastille du moteur, lue telle quelle : libellé commun, seconde pastille « Interrompu » au jour du calcul, anomalies en infobulle.
  lineState(order: Sales_order, line: Sales_order_l): { label: string; cls: string; interrupted: boolean; anomalies: string } | null {
    const view = this.lifecycles.get(order.id)?.view;
    const l = view?.lines.find(x => x.id === line.id);
    if (!view || !l) return null;
    return { label: statusLabel(l.status), cls: 'pill ' + statusClass(l.status), interrupted: showInterruptedBadge(l, view.as_of), anomalies: (l.anomalies ?? []).join('\n') };
  }

  orderAnomalies(order: Sales_order): string[] {
    return this.lifecycles.get(order.id)?.view?.anomalies ?? [];
  }

  /** « rangs facturés / Durée », la Durée absente ou ≤ 0 étant illimitée (même lecture que la fiche du dossier). */
  getLifecycleProgress(order: Sales_order, line: Sales_order_l): string {
    const l = this.lineLifecycle(order, line);
    if (!l) return '';
    const duration = Number(l.duration ?? 0);
    return `${l.rank} / ${duration > 0 ? duration : '∞'}`;
  }

  /** Durée atteinte selon le moteur (statut `finished`). */
  isLineExpired(order: Sales_order, line: Sales_order_l): boolean {
    return this.lineLifecycle(order, line)?.status === 'finished';
  }

  /** Jours de Paris écoulés depuis le dernier jour facturé d'une ligne dont la Durée est atteinte. */
  daysSinceExpiry(order: Sales_order, line: Sales_order_l): number | null {
    const l = this.lineLifecycle(order, line);
    const asOf = this.lifecycles.get(order.id)?.view?.as_of;
    if (l?.status !== 'finished' || !l.billed_until || !asOf) return null;
    const utc = (d: string) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
    return Math.max(0, Math.round((utc(asOf) - utc(l.billed_until)) / 86_400_000) - 1);
  }

  private readonly DATE_KEYS_METIER: string[] = Object.values(SalesOrderState)
    .filter(s => s !== SalesOrderState.MIGRATION)
    .map(s => getStateKey(s)!);

  private parseDate(raw: any): number {
    if (!raw) return NaN;
    if (raw instanceof Date) return raw.getTime();
    if (typeof raw === 'number') return raw;
    let s = String(raw).trim();
    if (/(?:[+-]\d{2})$/.test(s)) s += ':00';
    return new Date(s).getTime();
  }

  private getLastDateTime(p: Sales_order): number {
    const dates = p?.dates;
    if (!dates || typeof dates !== 'object') return NaN;
    let max = -Infinity;
    for (const key of this.DATE_KEYS_METIER) {
      const val = dates[key];
      if (val !== undefined && val !== null) {
        const t = this.parseDate(val);
        if (!isNaN(t) && t > max) max = t;
      }
    }
    return isFinite(max) ? max : NaN;
  }

  getLastDate(p: Sales_order): Date | undefined {
    const t = this.getLastDateTime(p);
    return isNaN(t) ? undefined : new Date(t);
  }

  public limitLabel(val: string): string {
    if (!val || val === this.SERVER_MAX) return '10\u00A0000 (maximum)';
    return val.replace(/\B(?=(\d{3})+$)/g, '\u00A0');
  }

  public get selectedStatusesLabel(): string {
    if (!this.selectedStatuses || this.selectedStatuses.length === 0) {
      return 'Tous (défaut)';
    }
    if (this.selectedStatuses.length === 1) {
      return getOrderLabel(this.selectedStatuses[0]);
    }
    return `${this.selectedStatuses.length} sélectionnés`;
  }
}
